import { hasAccess } from "@components/Authorization";
import { useConfig } from "@components/Config";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { isExternalInvestorPortalEnabled } from "@components/Organization";

const PATH_BACKSTAGE = "/backstage";

const LABEL_STAFF = "Staff";
const LABEL_SOURCES = "Bank Accounts";
const LABEL_LIAISONS = "Liaisons";
const LABEL_ORGANIZATION = "Organization";
const LABEL_INTEGRATION = "Integration";
const LABEL_MY_PROFILE = "My Profile";
const LABEL_EMAIL_IDENTITY = "Custom Domain";

export type SettingsLink = {
  title: string;
  path: string;
};

const useSettingsLinks = (): SettingsLink[] => {
  const hasCampaigns = useConfig("hasCampaigns");
  const { getOrganizationId } = useAppContext() as AppContext;

  const canViewSources = hasAccess(['bank-accounts-read', 'org-read']);
  const canViewMembers = hasAccess(['users-read', 'org-read']);
  const canViewSponsors = hasAccess(['sponsors-read']);
  const canViewOrganization = hasAccess(['org-read']);
  const canViewEmailIdentity = hasAccess(['org-read']) && hasCampaigns;
  const canViewIntegration = canViewOrganization
    && isExternalInvestorPortalEnabled(getOrganizationId());

  const items: SettingsLink[] = [];

  if (canViewOrganization) {
    items.push({
      title: LABEL_ORGANIZATION,
      path: `${PATH_BACKSTAGE}/settings/organization`,
    });
  }

  if (canViewMembers) {
    items.push({
      title: LABEL_STAFF,
      path: `${PATH_BACKSTAGE}/settings/members`,
    });
  }

  if (canViewSponsors) {
    items.push({
      title: LABEL_LIAISONS,
      path: `${PATH_BACKSTAGE}/settings/brokers`,
    });
  }

  if (canViewSources) {
    items.push({
      title: LABEL_SOURCES,
      path: `${PATH_BACKSTAGE}/settings/sources`,
    });
  }

  if (canViewEmailIdentity) {
    items.push({
      title: LABEL_EMAIL_IDENTITY,
      path: `${PATH_BACKSTAGE}/settings/email-identity`,
    });
  }

  items.push({
    title: LABEL_MY_PROFILE,
    path: `${PATH_BACKSTAGE}/settings/my-profile`,
  });

  if (canViewIntegration) {
    items.push({
      title: LABEL_INTEGRATION,
      path: `${PATH_BACKSTAGE}/settings/integration`,
    });
  }

  return items;
};

export default useSettingsLinks;
